import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { downloadFile } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { ICG2Guidelines } from '@fortem-monorepo/web-sdk'
import { ICGuidelines } from '@fortem-monorepo/web-sdk'

import { useUserContext } from '@/index'

export const useDownloadICGuidelinesExcelMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: async (params: ICGuidelines.GetICGuidelinesParams) => {
      const data = await api.icGuidelines.downloadExcel(params)

      const filename = `ic_guidelines_${params.value_date}.xlsx`

      downloadFile({
        fileData: data,
        filename,
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    },
    onSuccess: () => {
      toast.success('Download successful')
    },
  })
}

export const useForwardICGuidelinesAsEmailMutaiton = () => {
  const api = useApi()

  const { loggedInUser } = useUserContext()
  return useMutation({
    mutationFn: async (params: ICGuidelines.GetICGuidelinesParams) => {
      return api.icGuidelines.forwardEmail(params)
    },
    onSuccess: () => {
      toast.success(`Email sent to ${loggedInUser.user?.email}`)
    },
  })
}

export const useRunICGuidelinesMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: ICGuidelines.RunReportBody) => api.icGuidelines.runReports(params),
    onSuccess: () => {},
  })
}

export const useInvalidateGuidelines = () => {
  const queryClient = useQueryClient()

  return () => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ic-guidelines', 'guidelines'] }),
    ])
  }
}

export const useCreateGuidelineMutation = () => {
  const api = useApi()

  const invalidateGuidelines = useInvalidateGuidelines()

  return useMutation({
    mutationFn: (params: ICG2Guidelines.CreateGuidelineParams) => {
      return api.icGuidelines.v2.createGuideline(params)
    },
    onSuccess: () => {
      toast.success('Successfully created new guideline')
    },
    onSettled: () => {
      invalidateGuidelines()
    },
  })
}

export const useUpdateGuidelineMutation = () => {
  const api = useApi()

  const invalidateGuidelines = useInvalidateGuidelines()

  return useMutation({
    mutationFn: (params: ICG2Guidelines.UpdateGuidelineParams) => {
      return api.icGuidelines.v2.updateGuideline(params)
    },
    onSuccess: () => {
      toast.success('Guideline updated')
    },
    onSettled: () => {
      invalidateGuidelines()
    },
  })
}

export const useDeleteGuidelineMutation = () => {
  const api = useApi()

  const invalidateGuidelines = useInvalidateGuidelines()

  return useMutation({
    mutationFn: (params: { guideline_id: number }) => {
      return api.icGuidelines.v2.deleteGuideline(params)
    },
    onSuccess: () => {
      toast.success('Guideline deleted')
    },
    onSettled: () => {
      invalidateGuidelines()
    },
  })
}
